import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';

export default function FinalCtaSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-12 md:py-16 bg-background-50 overflow-hidden"
    >
      <FloatingShapes count={14} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={10} color="secondary" speed={0.5} />
      </div>
      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-100/80 rounded-full w-fit mx-auto mb-6">
              <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
              <span className="text-xs font-mono font-medium text-accent-700 uppercase tracking-wider">
                {t('home.final.badge')}
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-foreground-950 tracking-tight mb-4">
              {t('home.final.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-8">
              {t('home.final.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Link
                to="/solutions"
                className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#7057FF] text-white font-medium text-sm hover:bg-[#5a45d6] transition-colors"
              >
                {t('home.final.explore')}
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/how-we-work"
                className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-secondary-100 text-secondary-900 border border-secondary-200 font-medium text-sm hover:bg-secondary-200 transition-colors"
              >
                {t('home.final.howWeWork')}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}